import { useState } from 'react';
import {
    Badge,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Drawer,
    Button,
    useMediaQuery,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import {
    LayoutDashboard,
    Users,
    Warehouse,
    TrendingUp,
    Zap,
    Settings,
    ShieldCheck,
    UserPlus,
    FileText,
    CheckSquare,
    Calendar,
    Mail,
    Lock,
    MessageCircle,
    BookOpen,
    PenTool,
    BarChart3,
    LayoutGrid,
} from 'lucide-react';
import { CanAccess, useGetList } from 'react-admin';
import { Theme, useTheme } from '@mui/material/styles';
import { useChatBot } from './ChatBotWidget';

const DRAWER_WIDTH = 240;

interface SidebarProps {
    open?: boolean;
    onClose?: () => void;
}

// Shared nav item styles
const navItemBase = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    borderRadius: '8px',
    px: '12px',
    py: '8px',
    mx: '8px',
    mb: '2px',
    fontSize: '0.875rem',
    fontWeight: 500,
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.15s, color 0.15s',
};

const activeStyle = {
    backgroundColor: '#ecfdf5', // emerald-50
    color: '#047857', // emerald-700
    '& svg': { color: '#047857' },
};

const inactiveStyle = {
    color: '#475569', // slate-600
    '&:hover': {
        backgroundColor: '#f1f5f9', // slate-100
        color: '#0f172a', // slate-900
    },
    '& svg': { color: '#475569' },
    '&:hover svg': { color: '#0f172a' },
};

const teaserStyle = {
    color: '#94a3b8', // slate-400
    '&:hover': {
        color: '#64748b', // slate-500
        backgroundColor: '#f8fafc', // slate-50
    },
    '& svg': { color: '#94a3b8' },
    '&:hover svg': { color: '#64748b' },
};

interface NavItemProps {
    path: string;
    label: string;
    icon: React.ReactNode;
    selected: boolean;
    isMobile: boolean;
    onClose?: () => void;
    badge?: number;
}

function NavItem({
    path,
    label,
    icon,
    selected,
    isMobile,
    onClose,
    badge,
}: NavItemProps) {
    return (
        <Box
            component={Link}
            to={path}
            onClick={isMobile ? onClose : undefined}
            sx={{
                ...navItemBase,
                ...(selected ? activeStyle : inactiveStyle),
            }}
        >
            {icon}
            <Box component="span" sx={{ flex: 1, whiteSpace: 'nowrap' }}>
                {badge && badge > 0 ? (
                    <Badge
                        badgeContent={badge}
                        color="error"
                        sx={{
                            '& .MuiBadge-badge': {
                                right: -8,
                                top: 0,
                            },
                        }}
                    >
                        <span>{label}</span>
                    </Badge>
                ) : (
                    label
                )}
            </Box>
        </Box>
    );
}

interface TeaserNavItemProps {
    label: string;
    icon: React.ReactNode;
    isMobile: boolean;
    onClose?: () => void;
    onTeaserClick: (feature: string) => void;
}

function TeaserNavItem({
    label,
    icon,
    isMobile,
    onClose,
    onTeaserClick,
}: TeaserNavItemProps) {
    return (
        <Box
            component="button"
            onClick={() => {
                onTeaserClick(label);
                if (isMobile && onClose) onClose();
            }}
            sx={{
                ...navItemBase,
                ...teaserStyle,
                border: 'none',
                background: 'none',
                width: 'calc(100% - 16px)',
                textAlign: 'left',
            }}
        >
            {icon}
            <Box component="span" sx={{ flex: 1, whiteSpace: 'nowrap' }}>
                {label}
            </Box>
            <Lock size={12} />
        </Box>
    );
}

function AdminNavItem({
    path,
    label,
    icon,
    selected,
    isMobile,
    onClose,
}: Omit<NavItemProps, 'badge'>) {
    const { total = 0 } = useGetList('sales', {
        filter: { approved: false },
        pagination: { page: 1, perPage: 1 },
    });
    const pendingCount = total ?? 0;

    return (
        <NavItem
            path={path}
            label={label}
            icon={icon}
            selected={selected}
            isMobile={isMobile}
            onClose={onClose}
            badge={pendingCount}
        />
    );
}

const ICON_SIZE = 18;

function ChatNavItem({
    isMobile,
    onClose,
}: {
    isMobile: boolean;
    onClose?: () => void;
}) {
    const { open: chatOpen, toggle } = useChatBot();
    return (
        <Box
            component="button"
            onClick={() => {
                toggle();
                if (isMobile && onClose) onClose();
            }}
            sx={{
                ...navItemBase,
                ...(chatOpen ? activeStyle : inactiveStyle),
                border: 'none',
                background: 'none',
                width: 'calc(100% - 16px)',
                textAlign: 'left',
            }}
        >
            <MessageCircle size={ICON_SIZE} />
            <Box component="span" sx={{ flex: 1, whiteSpace: 'nowrap' }}>
                AI Assistant
            </Box>
        </Box>
    );
}

const Sidebar = ({ open = false, onClose }: SidebarProps) => {
    const location = useLocation();
    const theme = useTheme() as Theme;
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [teaserDialog, setTeaserDialog] = useState<string | null>(null);

    const isSelected = (path: string) => {
        if (path === '/') return location.pathname === '/';
        if (!location.pathname.startsWith(path)) return false;
        // Only highlight for list and create pages, not detail/show/edit sub-pages
        const rest = location.pathname.slice(path.length).replace(/^\//, '');
        if (rest && rest !== 'create') return false;
        return true;
    };

    const drawerContent = (
        <Box
            sx={{
                overflow: 'auto',
                py: 1,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
            }}
        >
            {/* Main nav section */}
            <Box>
                <CanAccess resource="dashboard" action="list">
                    <NavItem
                        path="/"
                        label="Dashboard"
                        icon={<LayoutDashboard size={ICON_SIZE} />}
                        selected={isSelected('/')}
                        isMobile={isMobile}
                        onClose={onClose}
                    />
                </CanAccess>

                <CanAccess resource="contacts" action="list">
                    <NavItem
                        path="/contacts"
                        label="Contacts"
                        icon={<Users size={ICON_SIZE} />}
                        selected={isSelected('/contacts')}
                        isMobile={isMobile}
                        onClose={onClose}
                    />
                </CanAccess>

                <CanAccess resource="accounts" action="list">
                    <NavItem
                        path="/accounts"
                        label="Accounts"
                        icon={<Warehouse size={ICON_SIZE} />}
                        selected={isSelected('/accounts')}
                        isMobile={isMobile}
                        onClose={onClose}
                    />
                </CanAccess>

                <CanAccess resource="quotes" action="list">
                    <NavItem
                        path="/quotes"
                        label="Growth Center"
                        icon={<TrendingUp size={ICON_SIZE} />}
                        selected={isSelected('/quotes')}
                        isMobile={isMobile}
                        onClose={onClose}
                    />
                </CanAccess>

                <CanAccess resource="admin-panel" action="list">
                    <AdminNavItem
                        path="/admin-panel"
                        label="Admin Dashboard"
                        icon={<ShieldCheck size={ICON_SIZE} />}
                        selected={isSelected('/admin-panel')}
                        isMobile={isMobile}
                        onClose={onClose}
                    />
                </CanAccess>
            </Box>

            {/* Separator */}
            <Divider sx={{ mx: 2, my: 1, borderColor: '#e2e8f0' }} />

            {/* Tools section */}
            <Box>
                <NavItem
                    path="/hail-pricing-tool"
                    label="Hail Pricing Tool"
                    icon={<Zap size={ICON_SIZE} />}
                    selected={isSelected('/hail-pricing-tool')}
                    isMobile={isMobile}
                    onClose={onClose}
                />

                <ChatNavItem isMobile={isMobile} onClose={onClose} />

                <CanAccess resource="rate-manager" action="list">
                    <NavItem
                        path="/rate-manager"
                        label="Rate Manager"
                        icon={<Settings size={ICON_SIZE} />}
                        selected={isSelected('/rate-manager')}
                        isMobile={isMobile}
                        onClose={onClose}
                    />
                </CanAccess>

                <NavItem
                    path="/hail-docs"
                    label="Hail Docs"
                    icon={<BookOpen size={ICON_SIZE} />}
                    selected={isSelected('/hail-docs')}
                    isMobile={isMobile}
                    onClose={onClose}
                />
            </Box>

            {/* Separator */}
            <Divider sx={{ mx: 2, my: 1, borderColor: '#e2e8f0' }} />

            {/* Teaser items */}
            <Box>
                <TeaserNavItem
                    label="Leads"
                    icon={<UserPlus size={ICON_SIZE} />}
                    isMobile={isMobile}
                    onClose={onClose}
                    onTeaserClick={setTeaserDialog}
                />
                <TeaserNavItem
                    label="Policies"
                    icon={<FileText size={ICON_SIZE} />}
                    isMobile={isMobile}
                    onClose={onClose}
                    onTeaserClick={setTeaserDialog}
                />
                <TeaserNavItem
                    label="Action Items"
                    icon={<CheckSquare size={ICON_SIZE} />}
                    isMobile={isMobile}
                    onClose={onClose}
                    onTeaserClick={setTeaserDialog}
                />
                <TeaserNavItem
                    label="Calendar"
                    icon={<Calendar size={ICON_SIZE} />}
                    isMobile={isMobile}
                    onClose={onClose}
                    onTeaserClick={setTeaserDialog}
                />
                <TeaserNavItem
                    label="Email"
                    icon={<Mail size={ICON_SIZE} />}
                    isMobile={isMobile}
                    onClose={onClose}
                    onTeaserClick={setTeaserDialog}
                />
                <TeaserNavItem
                    label="eSignature"
                    icon={<PenTool size={ICON_SIZE} />}
                    isMobile={isMobile}
                    onClose={onClose}
                    onTeaserClick={setTeaserDialog}
                />
                <TeaserNavItem
                    label="Reports"
                    icon={<BarChart3 size={ICON_SIZE} />}
                    isMobile={isMobile}
                    onClose={onClose}
                    onTeaserClick={setTeaserDialog}
                />
                <TeaserNavItem
                    label="Dashboards"
                    icon={<LayoutGrid size={ICON_SIZE} />}
                    isMobile={isMobile}
                    onClose={onClose}
                    onTeaserClick={setTeaserDialog}
                />
            </Box>

            {/* Spacer */}
            <Box sx={{ flex: 1 }} />

            {/* Upgrade to CropAMS dialog */}
            <Dialog
                open={teaserDialog !== null}
                onClose={() => setTeaserDialog(null)}
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        maxWidth: 400,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        fontWeight: 600,
                        color: '#0f172a',
                    }}
                >
                    {teaserDialog} is available with CropAMS
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#475569' }}>
                        Upgrade to CropAMS to unlock{' '}
                        {teaserDialog?.toLowerCase()} and the full suite of CRM
                        tools.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setTeaserDialog(null)}
                        sx={{
                            color: '#64748b',
                            textTransform: 'none',
                        }}
                    >
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        component="a"
                        href="https://www.cropams.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setTeaserDialog(null)}
                        sx={{
                            backgroundColor: '#10b981',
                            '&:hover': {
                                backgroundColor: '#059669',
                            },
                            textTransform: 'none',
                            borderRadius: '8px',
                        }}
                    >
                        Learn More
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );

    if (isMobile) {
        return (
            <Drawer
                variant="temporary"
                open={open}
                onClose={onClose}
                ModalProps={{
                    keepMounted: true,
                }}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: DRAWER_WIDTH,
                        boxSizing: 'border-box',
                        backgroundColor: '#FFFFFF',
                        borderRight: '1px solid #e2e8f0',
                        mt: 8,
                    },
                }}
            >
                {drawerContent}
            </Drawer>
        );
    }

    return (
        <Drawer
            variant="permanent"
            sx={{
                width: DRAWER_WIDTH,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: DRAWER_WIDTH,
                    boxSizing: 'border-box',
                    backgroundColor: '#FFFFFF',
                    borderRight: '1px solid #e2e8f0',
                    mt: 8,
                },
            }}
        >
            {drawerContent}
        </Drawer>
    );
};

export default Sidebar;
